<template>
  <el-dialog v-model="visible" title="笔记" :fullscreen="isMobile" width="960px" top="50px" destroy-on-close class="note-dialog" @open="loadData" @closed="handleClosed">
    <template #header v-if="isMobile">
      <div class="mobile-dialog-header">
        <span class="mobile-dialog-title">笔记</span>
        <el-button text :icon="Close" @click="visible = false" />
      </div>
    </template>

    <div class="note-layout" :class="{ 'is-mobile': isMobile }">
      <!-- 左侧：笔记列表 -->
      <div class="note-list-panel">
        <div class="note-category-bar">
          <el-select v-model="activeCategory" @change="onCategoryChange" size="small" placeholder="选择分类" class="category-select" clearable>
            <el-option label="全部" value="all" />
            <el-option v-for="cat in categories" :key="cat.id" :label="cat.name" :value="String(cat.id)">
              <span class="cat-tab-label">
                <span class="cat-dot" :style="{ background: cat.color }"></span>
                {{ cat.name }}
              </span>
            </el-option>
          </el-select>
          <el-button text :icon="Setting" size="small" @click="showCategoryManager = true" class="cat-manage-btn" />
        </div>

        <!-- 搜索 -->
        <div class="note-search">
          <el-input v-model="searchKey" placeholder="搜索笔记..." size="small" clearable @keyup.enter="fetchNoteList" @clear="fetchNoteList">
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </div>

        <!-- 笔记列表 -->
        <div class="note-items" v-if="noteList.length > 0">
          <div
            v-for="item in noteList"
            :key="item.id"
            class="note-item"
            :class="{ 'is-active': currentNoteId === item.id }"
            @click="selectNote(item)"
          >
            <div class="note-item-top">
              <span class="note-item-title">{{ item.title || '无标题' }}</span>
              <span v-if="getCategoryColor(item.category_id)" class="note-dot" :style="{ background: getCategoryColor(item.category_id) }"></span>
            </div>
            <div class="note-item-date">{{ item.note_date }}</div>
          </div>
        </div>
        <div v-else class="note-empty">
          <el-empty :image-size="60" description="暂无笔记" />
        </div>

        <div class="note-add-bar">
          <el-button type="primary" @click="handleAddNote" style="width:100%">+ 新建笔记</el-button>
        </div>
      </div>

      <!-- 右侧：笔记编辑器 -->
      <div class="note-editor-panel" v-if="currentNote">
        <div class="editor-header">
          <div class="editor-title-row">
            <el-input v-model="editForm.title" placeholder="笔记标题" class="title-input" />
            <div class="editor-actions">
              <el-button type="primary" @click="handleSave" :loading="saving">保存</el-button>
              <el-button type="danger" @click="handleDelete" :disabled="!currentNote.id">删除</el-button>
            </div>
          </div>
          <div class="editor-meta">
            <el-select v-model="editForm.category_id" placeholder="分类" size="small" style="width:140px">
              <el-option :value="0" label="未分类" />
              <el-option v-for="cat in categories" :key="cat.id" :value="cat.id" :label="cat.name" />
            </el-select>
            <el-date-picker v-model="editForm.note_date" type="date" placeholder="选择日期" value-format="YYYY-MM-DD" size="small" style="width:160px" />
          </div>
        </div>
        <div class="editor-content">
          <el-input
            v-model="editForm.content"
            type="textarea"
            :rows="isMobile ? 12 : 18"
            placeholder="开始记录..."
            class="content-input"
          />
        </div>
      </div>

      <!-- 未选中笔记时显示 -->
      <div class="note-editor-panel note-editor-empty" v-else>
        <el-empty :image-size="100" description="选择或新建一篇笔记" />
      </div>
    </div>

    <!-- 分类管理弹窗 -->
    <el-dialog v-model="showCategoryManager" title="分类管理" width="420px" :append-to-body="true" @closed="fetchCategories">
      <div class="category-list">
        <div v-for="cat in categories" :key="cat.id" class="category-item">
          <span class="cat-dot" :style="{ background: cat.color }"></span>
          <span class="cat-name">{{ cat.name }}</span>
          <div class="cat-actions">
            <el-button text :icon="Edit" size="small" @click="editCategory(cat)" />
            <el-button text :icon="Delete" size="small" type="danger" @click="delCategory(cat)" />
          </div>
        </div>
        <div v-if="categories.length === 0" class="category-empty">暂无分类</div>
      </div>
      <div class="category-add-row">
        <el-input v-model="newCatName" placeholder="分类名称" size="small" style="flex:1" />
        <el-color-picker v-model="newCatColor" size="small" />
        <el-button type="primary" size="small" @click="addCategory" :disabled="!newCatName.trim()">添加</el-button>
      </div>
      <div v-if="editingCat" class="category-edit-row">
        <span class="edit-label">编辑：</span>
        <el-input v-model="editingCat.name" placeholder="分类名称" size="small" style="flex:1" />
        <el-color-picker v-model="editingCat.color" size="small" />
        <el-button type="primary" size="small" @click="saveCategoryEdit">保存</el-button>
        <el-button size="small" @click="editingCat = null">取消</el-button>
      </div>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Close, Setting, Search, Edit, Delete } from '@element-plus/icons-vue'
import { getNoteList, getNoteDetail, addNote, updateNote, deleteNote, getNoteCategoryList, addNoteCategory, updateNoteCategory, deleteNoteCategory } from '@/api/noteApi'

export default {
  name: 'NoteDialog',
  props: {
    modelValue: Boolean
  },
  emits: ['update:modelValue'],
  components: { Search },
  setup(props, { emit }) {
    const visible = computed({
      get: () => props.modelValue,
      set: (val) => emit('update:modelValue', val)
    })

    const isMobile = ref(window.innerWidth <= 768)
    const checkMobile = () => { isMobile.value = window.innerWidth <= 768 }

    const categories = ref([])
    const activeCategory = ref('all')
    const searchKey = ref('')
    const noteList = ref([])
    const currentNoteId = ref(null)
    const currentNote = ref(null)
    const saving = ref(false)
    const showCategoryManager = ref(false)
    const newCatName = ref('')
    const newCatColor = ref('#409EFF')
    const editingCat = ref(null)

    const editForm = reactive({
      title: '',
      content: '',
      category_id: 0,
      note_date: ''
    })

    const fetchCategories = async () => {
      try {
        const res = await getNoteCategoryList()
        if (res.code === 200) categories.value = res.data || []
      } catch (e) { console.error(e) }
    }

    const fetchNoteList = async () => {
      try {
        const params = {}
        if (activeCategory.value !== 'all') params.category_id = Number(activeCategory.value)
        if (searchKey.value.trim()) params.keyword = searchKey.value.trim()
        const res = await getNoteList(params)
        if (res.code === 200) {
          noteList.value = (res.data.list || []).map(item => ({
            ...item,
            note_date: item.note_date || ''
          }))
        }
      } catch (e) { console.error(e) }
    }

    const loadData = async () => {
      await fetchCategories()
      await fetchNoteList()
    }

    const handleClosed = () => {
      currentNote.value = null
      currentNoteId.value = null
      searchKey.value = ''
      activeCategory.value = 'all'
      editForm.title = ''
      editForm.content = ''
      editForm.category_id = 0
      editForm.note_date = ''
    }

    const onCategoryChange = () => {
      fetchNoteList()
    }

    const getCategoryColor = (categoryId) => {
      const cat = categories.value.find(c => c.id === categoryId)
      return cat ? cat.color : ''
    }

    const selectNote = async (item) => {
      currentNoteId.value = item.id
      try {
        const res = await getNoteDetail(item.id)
        if (res.code === 200) {
          currentNote.value = res.data
          editForm.title = res.data.title || ''
          editForm.content = res.data.content || ''
          editForm.category_id = res.data.category_id || 0
          editForm.note_date = res.data.note_date || ''
        }
      } catch (e) { console.error(e) }
    }

    const handleAddNote = () => {
      currentNoteId.value = null
      currentNote.value = { id: null, title: '', content: '', category_id: 0, note_date: '' }
      editForm.title = ''
      editForm.content = ''
      editForm.category_id = 0
      editForm.note_date = new Date().toISOString().split('T')[0]
    }

    const handleSave = async () => {
      if (!editForm.title.trim()) {
        ElMessage.warning('请输入笔记标题')
        return
      }
      saving.value = true
      try {
        const payload = {
          title: editForm.title.trim(),
          content: editForm.content,
          category_id: editForm.category_id || 0,
          note_date: editForm.note_date || new Date().toISOString().split('T')[0]
        }
        if (currentNote.value?.id) {
          payload.id = currentNote.value.id
          const res = await updateNote(payload)
          if (res.code === 200) {
            ElMessage.success('保存成功')
            currentNote.value.title = payload.title
            currentNote.value.content = payload.content
            currentNote.value.category_id = payload.category_id
            currentNote.value.note_date = payload.note_date
            await fetchNoteList()
          } else {
            ElMessage.error(res.msg || '保存失败')
          }
        } else {
          const res = await addNote(payload)
          if (res.code === 200) {
            ElMessage.success('新建成功')
            const newId = res.data?.id
            if (newId) {
              noteList.value = [{ id: newId, title: payload.title, category_id: payload.category_id, note_date: payload.note_date }, ...noteList.value]
              await selectNote({ id: newId })
            }
            await fetchNoteList()
          } else {
            ElMessage.error(res.msg || '新建失败')
          }
        }
      } catch (e) {
        ElMessage.error('操作失败')
      } finally {
        saving.value = false
      }
    }

    const handleDelete = async () => {
      if (!currentNote.value?.id) return
      try {
        await ElMessageBox.confirm('确定删除此笔记？', '提示', { type: 'warning' })
        const res = await deleteNote(currentNote.value.id)
        if (res.code === 200) {
          ElMessage.success('删除成功')
          currentNote.value = null
          currentNoteId.value = null
          await fetchNoteList()
        }
      } catch (e) {
        // cancelled
      }
    }

    const addCategory = async () => {
      const name = newCatName.value.trim()
      if (!name) return
      try {
        const res = await addNoteCategory({ name, color: newCatColor.value })
        if (res.code === 200) {
          ElMessage.success('新增分类成功')
          newCatName.value = ''
          newCatColor.value = '#409EFF'
          await fetchCategories()
        }
      } catch (e) {
        ElMessage.error('新增失败')
      }
    }

    const editCategory = (cat) => {
      editingCat.value = { id: cat.id, name: cat.name, color: cat.color }
    }

    const saveCategoryEdit = async () => {
      if (!editingCat.value?.name?.trim()) return
      try {
        const res = await updateNoteCategory({
          id: editingCat.value.id,
          name: editingCat.value.name.trim(),
          color: editingCat.value.color
        })
        if (res.code === 200) {
          ElMessage.success('修改成功')
          editingCat.value = null
          await fetchCategories()
        }
      } catch (e) {
        ElMessage.error('修改失败')
      }
    }

    const delCategory = async (cat) => {
      try {
        await ElMessageBox.confirm(`确定删除分类「${cat.name}」？`, '提示', { type: 'warning' })
        const res = await deleteNoteCategory(cat.id)
        if (res.code === 200) {
          ElMessage.success('删除成功')
          await fetchCategories()
        }
      } catch (e) {
        // cancelled
      }
    }

    onMounted(() => {
      window.addEventListener('resize', checkMobile)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', checkMobile)
    })

    return {
      visible, isMobile, categories, activeCategory, searchKey,
      noteList, currentNoteId, currentNote, saving, editForm,
      showCategoryManager, newCatName, newCatColor, editingCat,
      fetchNoteList, onCategoryChange, getCategoryColor,
      selectNote, handleAddNote, handleSave, handleDelete,
      addCategory, editCategory, saveCategoryEdit, delCategory,
      fetchCategories, loadData, handleClosed,
      Close, Setting, Search, Edit, Delete
    }
  }
}
</script>

<style scoped>
.note-dialog {
  --note-left-width: 320px;
}
.mobile-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
  width: 100%;
}
.mobile-dialog-title {
  font-size: 17px;
  font-weight: 600;
  color: #303133;
}

.note-layout {
  display: flex;
  gap: 0;
  min-height: 480px;
}
.note-layout.is-mobile {
  flex-direction: column;
  min-height: 0;
}

/* 左侧面板 */
.note-list-panel {
  width: var(--note-left-width);
  flex-shrink: 0;
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  padding-right: 12px;
}
.note-layout.is-mobile .note-list-panel {
  width: 100%;
  border-right: none;
  border-bottom: 1px solid #f0f0f0;
  padding-right: 0;
  padding-bottom: 8px;
}

.note-category-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
  max-width: 100%;
  overflow: hidden;
}
.cat-tab-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.cat-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.cat-manage-btn {
  flex-shrink: 0;
}
.category-select {
  flex: 1;
  min-width: 0;
}
.category-select :deep(.el-select__wrapper) {
  font-size: 12px;
  min-height: 30px;
}
.category-select :deep(.el-select__placeholder) {
  font-size: 12px;
}

.note-search {
  margin-bottom: 8px;
}

.note-items {
  flex: 1;
  overflow-y: auto;
  max-height: 360px;
}
.note-item {
  padding: 10px 8px;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 4px;
  transition: background 0.15s;
  border-left: 3px solid transparent;
}
.note-item:hover {
  background: #f5f7fa;
}
.note-item.is-active {
  background: #ecf5ff;
  border-left-color: #409EFF;
}
.note-item-top {
  display: flex;
  align-items: center;
  gap: 6px;
}
.note-item-title {
  font-size: 13px;
  color: #303133;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.note-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.note-item-date {
  font-size: 11px;
  color: #bbb;
  margin-top: 2px;
}
.note-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.note-add-bar {
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  margin-top: auto;
}

/* 右侧编辑面板 */
.note-editor-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-left: 16px;
  min-width: 0;
}
.note-editor-panel.note-editor-empty {
  align-items: center;
  justify-content: center;
}
.note-layout.is-mobile .note-editor-panel {
  padding-left: 0;
}

.editor-header {
  margin-bottom: 12px;
}
.editor-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.title-input {
  flex: 1;
}
.title-input :deep(.el-input__inner) {
  font-size: 16px;
  font-weight: 600;
}
.editor-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.editor-meta {
  display: flex;
  gap: 8px;
}
.editor-content {
  flex: 1;
  display: flex;
}
.content-input {
  flex: 1;
}
.content-input :deep(.el-textarea__inner) {
  height: 100% !important;
  font-size: 14px;
  line-height: 1.8;
  resize: none;
}

/* 分类管理 */
.category-list {
  margin-bottom: 12px;
  max-height: 260px;
  overflow-y: auto;
}
.category-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  border-bottom: 1px solid #f5f5f5;
}
.category-item:last-child {
  border-bottom: none;
}
.category-name {
  flex: 1;
  font-size: 14px;
}
.category-actions {
  display: flex;
  gap: 2px;
}
.category-empty {
  text-align: center;
  padding: 20px 0;
  color: #999;
  font-size: 13px;
}
.category-add-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-top: 1px solid #f0f0f0;
}
.category-edit-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-top: 1px solid #f0f0f0;
}
.edit-label {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .note-layout {
    flex-direction: column;
  }
  .note-list-panel {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #f0f0f0;
    padding-right: 0;
    padding-bottom: 8px;
  }
  .note-editor-panel {
    padding-left: 0;
  }
}
</style>
